import "./component-gift-with-purchase.js";

// Clean Modular Custom JS Scripts
// Avoid polluting the global namespace
window.ShopifyCustom = window.ShopifyCustom || {};

ShopifyCustom.init = function () {
  this.runSafe("visualNavRouter", this.visualNavRouter);
  this.runSafe("setupScrollObservers", this.setupScrollObservers);
  this.runSafe(
    "updateConstructorStickyAttributes",
    this.updateConstructorStickyAttributes
  );
  this.runSafe(
    "handleWaitlistButtonVisibility",
    this.handleWaitlistButtonVisibility
  );
  this.runSafe(
    "toggleMorePaymentOptionsApplePayMobileOnly",
    this.toggleMorePaymentOptionsApplePayMobileOnly
  );
  this.runSafe("setVariantOnLoad", this.setVariantOnLoad);
  this.runSafe("backToTop", this.backToTop);
  this.runSafe("initNativeLazyLoadBlurUp", this.initNativeLazyLoadBlurUp);
  this.runSafe("addSortModalCloseButton", this.addSortModalCloseButton);
  this.runSafe(
    "replaceHistoryForCombinedListingSwatchClicks",
    this.replaceHistoryForCombinedListingSwatchClicks
  );
  this.runSafe(
    "preventEmptyHashHistoryEntries",
    this.preventEmptyHashHistoryEntries
  );
  //this.runSafe("removeBodyInlineStyles", this.removeBodyInlineStyles);
  //this.runSafe("removeHtmlInlineHtmlStyles", this.removeHtmlInlineHtmlStyles);
};

/**
 * Empty `href="#"` anchors (Shopify more-payment-options, Visually fit-bar, etc.)
 * push a history entry for `/product#`. Back then only strips the hash before
 * returning to the collection. Prevent default and clean a trailing empty hash.
 */
ShopifyCustom.preventEmptyHashHistoryEntries = function () {
  if (window.__VPVEmptyHashGuardReady) return;
  window.__VPVEmptyHashGuardReady = true;

  const stripEmptyHashFromUrl = () => {
    if (window.location.hash !== "" || !window.location.href.endsWith("#")) {
      return;
    }
    const cleanUrl = window.location.pathname + window.location.search;
    window.history.replaceState(
      window.history.state,
      "",
      cleanUrl || window.location.pathname
    );
  };

  // Capture as early as possible so injected widgets can't pollute history.
  // Only block navigation — do not scroll or otherwise handle the click.
  document.addEventListener(
    "click",
    (event) => {
      const anchor = event.target.closest?.('a[href="#"]');
      if (!anchor) return;
      event.preventDefault();
    },
    true
  );

  stripEmptyHashFromUrl();
  window.addEventListener("load", stripEmptyHashFromUrl);
};

/**
 * PDP requirement:
 * - Desktop: always hide "More payment options"
 * - Mobile: show "More payment options" only when Apple Pay is available/visible
 */
ShopifyCustom.toggleMorePaymentOptionsApplePayMobileOnly = function () {
  if (!document?.body?.classList?.contains("template--product")) return;

  const MORE_OPTIONS_SELECTOR = ".more-payment-options-container";
  const APPLE_PAY_SELECTORS = [
    // Faherty custom Apple Pay (must have apple-pay-enabled class to be considered visible)
    ".custom-apple-pay-btn.apple-pay-enabled",
    // Shopify native Apple Pay (theme variations)
    ".shopify-payment-button__button--apple-pay",
    'shopify-accelerated-checkout [aria-label*="Apple Pay"]',
    'shopify-accelerated-checkout [aria-label*="ApplePay"]',
  ];

  const canUseApplePay = () => {
    try {
      return (
        window.ApplePaySession &&
        typeof window.ApplePaySession.canMakePayments === "function" &&
        window.ApplePaySession.canMakePayments()
      );
    } catch (_e) {
      return false;
    }
  };

  const isVisible = (el) => {
    if (!el) return false;
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden") return false;
    return el.getClientRects().length > 0;
  };

  const hasVisibleApplePayButton = () => {
    for (const sel of APPLE_PAY_SELECTORS) {
      const els = document.querySelectorAll(sel);
      for (const el of els) {
        if (isVisible(el)) return true;
      }
    }
    return false;
  };

  const update = () => {
    const containers = document.querySelectorAll(MORE_OPTIONS_SELECTOR);
    if (!containers.length) return;

    const isDesktop = window.matchMedia("(min-width: 769px)").matches;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    // Desktop: always hidden
    if (isDesktop) {
      containers.forEach((c) => {
        c.hidden = true;
      });
      return;
    }

    // Mobile: only show when Apple Pay button is actually visible
    // We check for visible buttons rather than just availability to ensure the button is rendered
    const shouldShow = isMobile && hasVisibleApplePayButton();
    containers.forEach((c) => {
      c.hidden = !shouldShow;
    });
  };

  // Initial + delayed checks (Apple Pay init is async in this theme)
  update();
  setTimeout(update, 250);
  setTimeout(update, 1250);

  // Watch for product rerenders / payment button swaps.
  const root =
    document.querySelector("#main-product-info") || document.body || document;
  let raf = null;
  const schedule = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = null;
      update();
    });
  };

  window.addEventListener("resize", schedule, { passive: true });
  
  // Listen for custom event when Apple Pay button is enabled
  document.addEventListener("apple-pay-button-enabled", schedule, { passive: true });

  const observer = new MutationObserver(schedule);
  observer.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["style", "class", "hidden", "aria-hidden"],
  });
};

ShopifyCustom.runSafe = function (name, fn) {
  try {
    fn();
  } catch (err) {
    console.warn(`Test Failed: [custom.js] Failed ${name}:`, err);
  }
};

/**
 * Combined Shopify listings render color swatches as real <a href> navigations.
 * Each click pushes a new history entry, so "Back" cycles through colors instead
 * of returning to the collection. Replacing the current history entry matches
 * in-page variant behavior (replaceState) and restores expected Back UX.
 */
ShopifyCustom.replaceHistoryForCombinedListingSwatchClicks = function () {
  const COMBINED_LISTING_SWATCH_ANCHOR_SELECTOR =
    "a.product-swatch, a.thumbnail-swatch, a.block-swatch, a.popover-listbox__option";

  const isPlainLeftClick = (event) =>
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey;

  document.addEventListener(
    "click",
    (event) => {
      if (!isPlainLeftClick(event)) return;

      const anchor = event.target.closest(COMBINED_LISTING_SWATCH_ANCHOR_SELECTOR);
      if (!anchor || anchor.getAttribute("target") === "_blank") return;

      const variantPicker = anchor.closest("variant-picker[update-url]");
      if (!variantPicker) return;

      const hrefAttr = anchor.getAttribute("href");
      if (!hrefAttr || hrefAttr.startsWith("#") || hrefAttr.startsWith("javascript:")) {
        return;
      }

      let dest;
      try {
        dest = new URL(anchor.href);
      } catch (_e) {
        return;
      }

      if (dest.origin !== window.location.origin) return;
      if (!dest.pathname.includes("/products/")) return;

      event.preventDefault();
      window.location.replace(anchor.href);
    },
    true
  );
};

ShopifyCustom.visualNavRouter = function () {
  const nav = document.querySelector(".js-router-enabled");
  const component = document.querySelector(".react-plp-grid");
  if (!nav || !component) return;

  // Flag to avoid false-positive popstate on page load
  let didPushState = false;

  nav.addEventListener("click", (e) => {
    const link = e.target.closest("a");
    if (!link || !nav.contains(link)) return;

    e.preventDefault();

    nav.querySelectorAll("a.isActive").forEach((activeLink) => {
      if (activeLink !== link) {
        activeLink.classList.remove("isActive");
      }
    });

    link.classList.add("isActive");

    const newUrl = new URL(link.getAttribute("href"), window.location.origin);

    // Keep active filters/sort when switching collections; reset to page 1
    const currentParams = new URLSearchParams(window.location.search);
    const preserved = new URLSearchParams();
    currentParams.forEach((value, key) => {
      if (key.startsWith("filters[") || key === "sortBy" || key === "sortOrder") {
        preserved.append(key, value);
      }
    });
    if ([...preserved.keys()].length) {
      preserved.set("page", "1");
    }

    const qs = preserved.toString();
    const fullPath = newUrl.pathname + (qs ? `?${qs}` : "");

    // Push state
    history.pushState({}, "", fullPath);
    didPushState = true;

    // Trigger re-render in React
    const currentVal = parseInt(component.getAttribute("data-increment")) || 0;
    component.setAttribute("data-increment", currentVal + 1);
  });

  // Handle back/forward
  window.addEventListener("popstate", function () {
    // Only trigger reload on user navigation (not page load)
    if (didPushState) {
      window.location.reload();
    } else {
      // This is the first popstate (possibly on load), ignore and flip flag
      didPushState = true;
    }
  });
};

ShopifyCustom.setupScrollObservers = function () {
  const targets = document.querySelectorAll(".js-scroll-observer");

  if (!targets.length) return;

  const observer = new IntersectionObserver(
    (entries, observerInstance) => {
      entries.forEach((entry) => {
        const el = entry.target;
        if (
          entry.isIntersecting &&
          entry.intersectionRatio >= 0.5 &&
          entry.boundingClientRect.top > 0 // ensures it's scrolling *down* into view
        ) {
          el.classList.add("observerIsVisible");
          observerInstance.unobserve(el); // stop observing once it's visible
        }
      });
    },
    {
      threshold: 0.5, // trigger when 50% of the element is in view
    }
  );
  targets.forEach((el) => observer.observe(el));
};

ShopifyCustom.removeHtmlInlineHtmlStyles = function () {
  const htmlEl = document.documentElement;
  if (!htmlEl) return;

  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (
        mutation.type === "attributes" &&
        mutation.attributeName === "style"
      ) {
        const newValue = htmlEl.getAttribute("style");
        console.log("[MutationObserver] <html> style changed:", newValue);

        requestAnimationFrame(() => {
          const currentStyle = htmlEl.getAttribute("style");
          if (!currentStyle) {
            console.log("[Cleaner] No inline style to clean.");
            return;
          }

          const cleanedRules = [];
          const removedProps = [];

          currentStyle
            .split(";")
            .map((rule) => rule.trim())
            .forEach((rule) => {
              if (!rule) return;
              const [prop] = rule.split(":");
              if (
                ["overflow", "inset", "position"].includes(
                  prop?.trim().toLowerCase()
                )
              ) {
                removedProps.push(prop.trim());
              } else {
                cleanedRules.push(rule);
              }
            });

          if (removedProps.length > 0) {
            if (cleanedRules.length > 0) {
              htmlEl.setAttribute("style", cleanedRules.join("; ") + ";");
            } else {
              htmlEl.removeAttribute("style");
            }

            console.log(
              `[Cleaner] Removed from <html> style: ${removedProps.join(", ")}`
            );
            console.log("[Cleaner] Remaining styles:", cleanedRules.join("; "));
          } else {
            console.log("[Cleaner] No matching styles to remove.");
          }
        });
      }
    }
  });

  observer.observe(htmlEl, {
    attributes: true,
    attributeOldValue: true,
    attributeFilter: ["style"],
  });

  console.log("[Observer] Started watching <html> for inline style changes.");
};

ShopifyCustom.removeBodyInlineStyles = function () {
  const bodyEl = document.body;
  if (!bodyEl) return;

  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (
        mutation.type === "attributes" &&
        mutation.attributeName === "style"
      ) {
        setTimeout(() => {
          console.log("Removing inline styles from body");
          bodyEl.removeAttribute("style");
        }, 10);
      }
    }
  });

  observer.observe(bodyEl, {
    attributes: true,
    attributeFilter: ["style"],
  });
};

ShopifyCustom.updateConstructorStickyAttributes = function () {
  const stickyEl = document.getElementById("constructor-product-info");
  if (!stickyEl) return;

  document.addEventListener("variant:change", function (event) {
    const variant = event.detail?.variant;
    if (!variant) return;

    stickyEl.setAttribute("data-cnstrc-item-variation-id", variant.id);
    const dollars = variant.price / 100;
    stickyEl.setAttribute(
      "data-cnstrc-item-price",
      dollars % 1 === 0 ? dollars.toString() : dollars.toFixed(2)
    );
  });
};

ShopifyCustom.handleWaitlistButtonVisibility = function () {
  // Helper function to update button visibility
  function updateButtonState(variant) {
    // Skip if this is a product with grouping (they handle their own waitlist logic)
    const productGrouping = document.querySelector("product-grouping");
    if (productGrouping) {
      // ProductGrouping handles its own button state management
      return;
    }

    // Find the product form and buttons
    const addToCartForm = document.querySelector(
      ".product-info .shopify-product-form"
    );
    if (!addToCartForm) return;

    const addToCartButton = addToCartForm.querySelector(
      'buy-buttons button[type="submit"]'
    );
    const notifyMeBtn = addToCartForm.querySelector(
      "buy-buttons button[data-bis-trigger]"
    );
    const addToCartButtonText = addToCartButton?.querySelector("div");
    const soldOutElement = document.querySelector(
      ".product-info sold-out-badge"
    );

    if (!addToCartButton || !notifyMeBtn || !addToCartButtonText) return;

    const isPreorder = window.isPreorderProduct?.() || false;

    if (variant.available) {
      // Variant is available - show add to cart, hide waitlist
      // Primary: format price from variant data (always accurate at variant:change time,
      // avoids race condition where the DOM price-list still shows the previous variant's price)
      let priceText = "";

      if (typeof variant.price !== "undefined" && variant.price !== null) {
        const cents = Number(variant.price);
        if (!isNaN(cents)) {
          const dollars = cents / 100;
          priceText =
            "$" +
            (dollars % 1 === 0
              ? String(Math.round(dollars))
              : dollars.toFixed(2));
        }
      }

      // Fallback: read from displayed price-list element (when variant.price is unavailable)
      if (!priceText) {
        const productInfo = document.querySelector(".product-info, [class*='product-info']");
        const priceList = productInfo
          ? productInfo.querySelector("price-list.price-list")
          : document.querySelector("price-list.price-list");

        if (priceList) {
          const salePriceEl = priceList.querySelector("sale-price");
          const priceElement = salePriceEl || priceList.querySelector(".text-subdued");

          if (priceElement) {
            let priceTextContent = priceElement.textContent?.trim() || "";
            const srOnlySpans = priceElement.querySelectorAll(".sr-only");
            srOnlySpans.forEach(span => {
              priceTextContent = priceTextContent.replace(span.textContent, "").trim();
            });
            const priceMatch = priceTextContent.match(/\$?[\d,]+(?:\.\d{2})?/);
            if (priceMatch) {
              priceText = priceMatch[0];
              if (!priceText.startsWith("$")) {
                priceText = "$" + priceText;
              }
            }
          }
        }
      }

      if (addToCartButtonText) {
        if (isPreorder) {
          addToCartButtonText.textContent = priceText
            ? `PREORDER – ${priceText}`
            : "PREORDER";
        } else {
          addToCartButtonText.textContent = priceText
            ? `Add to Bag – ${priceText}`
            : "Add to Bag";
        }
      }

      if (addToCartButton) {
        addToCartButton.removeAttribute("disabled");
        addToCartButton.classList.remove("button--subdued");
        addToCartButton.setAttribute("type", "submit");
        addToCartButton.style.setProperty("display", "block", "important");
        addToCartButton.style.setProperty("visibility", "visible", "important");
        addToCartButton.style.setProperty("opacity", "1", "important");
        addToCartButton.style.removeProperty("pointer-events");
      }

      if (notifyMeBtn) {
        notifyMeBtn.style.setProperty("display", "none", "important");
        notifyMeBtn.style.setProperty("visibility", "hidden", "important");
        notifyMeBtn.style.setProperty("opacity", "0", "important");
      }
      if (soldOutElement) soldOutElement.classList.add("is-hidden");
    } else {
      // Variant is sold out - show waitlist, hide add to cart
      if (addToCartButton) {
        addToCartButton.setAttribute("disabled", "disabled");
        addToCartButton.classList.add("button--subdued");
        addToCartButton.setAttribute("type", "button");
        addToCartButton.style.setProperty("display", "none", "important");
        addToCartButton.style.setProperty("visibility", "hidden", "important");
        addToCartButton.style.setProperty("opacity", "0", "important");
        addToCartButton.style.setProperty(
          "pointer-events",
          "none",
          "important"
        );
      }
      if (addToCartButtonText) {
        addToCartButtonText.textContent = "Sold Out";
      }
      if (notifyMeBtn) {
        notifyMeBtn.style.setProperty("display", "block", "important");
        notifyMeBtn.style.setProperty("visibility", "visible", "important");
        notifyMeBtn.style.setProperty("opacity", "1", "important");
      }
      if (soldOutElement) {
        soldOutElement.classList.remove("is-hidden");
      }
    }
  }

  // Listen for variant changes
  document.addEventListener("variant:change", function (event) {
    // Skip if this is a product with grouping (they handle their own button state)
    const productGrouping = document.querySelector("product-grouping");
    if (productGrouping) {
      // ProductGrouping handles its own button state management
      // Still notify AMP Back in Stock app though
      const variant = event.detail?.variant;
      if (variant) {
        notifyAmpBackInStock(variant);
      }
      return;
    }
    
    const variant = event.detail?.variant;
    if (!variant) return;

    updateButtonState(variant);

    // Notify AMP Back in Stock app about variant change
    notifyAmpBackInStock(variant);
  });
  
  // Also listen for variant input changes (catches when variant picker updates from URL)
  document.addEventListener("DOMContentLoaded", function() {
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput) {
      // Listen for changes to the variant input
      variantInput.addEventListener("change", function() {
        // Skip if this is a product with grouping
        const productGrouping = document.querySelector("product-grouping");
        if (productGrouping) {
          return;
        }
        
        // Get variant from variant picker script
        const variantPicker = document.querySelector("variant-picker");
        if (variantPicker) {
          const variantScript = variantPicker.querySelector("script[data-variant]");
          if (variantScript) {
            try {
              const variant = JSON.parse(variantScript.textContent || "{}");
              if (variant && variant.id && variant.id.toString() === variantInput.value) {
                updateButtonState(variant);
              }
            } catch (e) {
              console.warn("Could not parse variant data from input change:", e);
            }
          }
        }
      });
    }
  });

  // Function to notify AMP Back in Stock app about variant changes
  function notifyAmpBackInStock(variant) {
    try {
      // Method 1: Update the variant input that the app might be watching
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput && variant.id) {
        variantInput.value = variant.id;
        // Trigger change event to notify any listeners
        variantInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      // Method 2: Try common AMP Back in Stock methods if they exist
      if (
        window.AMP &&
        window.AMP.BackInStock &&
        typeof window.AMP.BackInStock.selectVariant === "function"
      ) {
        window.AMP.BackInStock.selectVariant(variant.id);
      } else if (
        window.ampBackInStock &&
        typeof window.ampBackInStock.selectVariant === "function"
      ) {
        window.ampBackInStock.selectVariant(variant.id);
      }

      // Method 3: Dispatch custom event that the app might listen for
      window.dispatchEvent(
        new CustomEvent("amp:variant:change", {
          detail: {
            variant: variant,
            productId: variant.product_id || window.meta?.product?.id,
          },
        })
      );

      // Method 4: Update any data attributes that the app might read
      const bisButtons = document.querySelectorAll("[data-bis-trigger]");
      bisButtons.forEach((button) => {
        button.setAttribute("data-variant-id", variant.id);
        button.setAttribute(
          "data-product-id",
          variant.product_id || window.meta?.product?.id || ""
        );
      });
    } catch (error) {
      console.warn("Error notifying AMP Back in Stock app:", error);
    }
  }

  /**
   * AMP Back in Stock only calls createUI() after it finds sold-out variants in
   * product.js. When Storefront availableForSale is false but product.js still
   * reports available (e.g. store-only inventory), BIS never builds its form and
   * clicks throw: Cannot read properties of undefined (reading 'selectVariant').
   *
   * Even after createUI(), show() calls manageUpsellCrossSellBar() which does
   * recommendedProducts.forEach(...) — that crashes if recommendations never loaded.
   * The modal dropdown is also built from popup.variants (unavailable only), so we
   * must seed the selected variant before createUI or the select is empty.
   */
  function resolveBisVariantId(bisTrigger) {
    const fromBtn = bisTrigger?.getAttribute("data-variant-id");
    if (fromBtn) return String(fromBtn);

    const variantInput = document.querySelector(
      'buy-buttons input[name="id"], form[action*="/cart/add"] input[name="id"], input[name="id"]'
    );
    if (variantInput?.value) return String(variantInput.value);

    const activeSize = document.querySelector(
      ".product-grouping__options.is-size .product-option.is-active[data-variant-id]"
    );
    if (activeSize?.dataset?.variantId) {
      return String(activeSize.dataset.variantId);
    }

    try {
      const fromUrl = new URLSearchParams(window.location.search).get("variant");
      if (fromUrl) return String(fromUrl);
    } catch (e) {
      /* ignore */
    }

    return "";
  }

  function cloneBisVariant(variant) {
    if (!variant) return null;
    if (typeof window.BIS?.extend === "function") {
      return window.BIS.extend({}, variant);
    }
    return { ...variant };
  }

  function seedBisUnavailableVariant(popup, variantId) {
    if (!popup) return false;
    if (!Array.isArray(popup.variants)) popup.variants = [];

    const id = variantId ? parseInt(variantId, 10) : NaN;
    if (Number.isNaN(id)) return false;

    const alreadyPresent = popup.variants.some(
      (v) => parseInt(v?.id, 10) === id
    );
    if (alreadyPresent) return true;

    const fromMap =
      popup._variantsById?.[id] || popup._variantsById?.[String(id)];
    const fromProduct = popup.product?.variants?.find(
      (v) => parseInt(v?.id, 10) === id
    );
    const seeded = cloneBisVariant(fromMap || fromProduct);
    if (!seeded) return false;

    // Ensure the modal treats it as waitlist-eligible
    seeded.available = false;
    popup.variants.push(seeded);
    return true;
  }

  function destroyBisForm(popup) {
    if (!popup?.form) return;
    try {
      popup.form.frame?.remove?.();
      popup.form.wrap?.remove?.();
      const frameDoc = popup.form.frameDoc?.();
      frameDoc?.defaultView?.frameElement?.remove?.();
    } catch (e) {
      /* ignore cleanup errors */
    }
    popup.form = null;
  }

  function bisFormHasVariantOption(popup, variantId) {
    try {
      const select = popup?.form?.frameDoc?.()?.querySelector?.("#variants");
      if (!select) return false;
      if (!variantId) return select.options.length > 0;
      return Array.from(select.options).some(
        (option) => String(option.value) === String(variantId)
      );
    } catch (e) {
      return false;
    }
  }

  function ensureBisFormReady(variantId) {
    const popup = window.BIS?.popup;
    if (!popup || typeof popup.createUI !== "function") return null;

    seedBisUnavailableVariant(popup, variantId);

    // show() → manageUpsellCrossSellBar() assumes an array; avoid forEach crash
    if (!Array.isArray(popup.recommendedProducts)) {
      popup.recommendedProducts = [];
    }

    const needsRebuild =
      popup.form && !bisFormHasVariantOption(popup, variantId);
    if (needsRebuild) {
      destroyBisForm(popup);
    }

    if (!popup.form) {
      try {
        popup.createUI();
      } catch (e) {
        console.warn("[BIS] Failed to create waitlist form:", e);
        return null;
      }
    }

    return popup.form ? popup : null;
  }

  function openBisWaitlist(variantId) {
    const popup = ensureBisFormReady(variantId);
    if (!popup?.form) return false;

    const id = variantId ? parseInt(variantId, 10) : NaN;
    try {
      if (!Number.isNaN(id) && typeof popup.form.selectVariant === "function") {
        popup.form.selectVariant(id);
      }
      if (typeof popup.show === "function") {
        popup.show(Number.isNaN(id) ? {} : { variantId: id });
      } else if (typeof popup.toggle === "function") {
        popup.toggle(false);
      }
      return true;
    } catch (e) {
      console.warn("[BIS] Failed to open waitlist modal:", e);
      return false;
    }
  }

  function shouldHandleBisClickFallback(popup) {
    if (!popup) return false;
    // Form never created, or created without usable unavailable variants
    return !popup.form || !Array.isArray(popup.variants) || popup.variants.length === 0;
  }

  // Capture-phase: set variant attrs, and open BIS ourselves when its form was never created
  document.addEventListener(
    "click",
    function (event) {
      if (!event.target || typeof event.target.closest !== "function") {
        return;
      }

      const bisTrigger = event.target.closest(
        "[data-bis-trigger], #BIS_trigger"
      );
      if (!bisTrigger) return;

      const variantId = resolveBisVariantId(bisTrigger);
      if (variantId) {
        bisTrigger.setAttribute("data-variant-id", variantId);
        bisTrigger.setAttribute(
          "data-product-id",
          String(window.meta?.product?.id || "")
        );
      }

      const popup = window.BIS?.popup;
      if (!shouldHandleBisClickFallback(popup)) return;

      // Form was never created (no OOS variants in product.js). Open it ourselves.
      event.preventDefault();
      event.stopImmediatePropagation();
      openBisWaitlist(variantId);
    },
    true
  );

  // Check initial state on page load
  document.addEventListener("DOMContentLoaded", function () {
    // Skip if this is a product with grouping (they handle their own button state)
    const productGrouping = document.querySelector("product-grouping");
    if (productGrouping) {
      // ProductGrouping handles its own button state management
      return;
    }
    
    // Get initial variant data and update button from displayed price
    function tryUpdateButton() {
      // Get variant from variant picker script to check availability
      const variantPicker = document.querySelector("variant-picker");
      if (variantPicker) {
        const variantScript = variantPicker.querySelector("script[data-variant]");
        if (variantScript) {
          try {
            const variant = JSON.parse(variantScript.textContent || "{}");
            // Check if variant input matches the script variant
            const variantInput = document.querySelector('input[name="id"]');
            if (variant && variant.id) {
              // If variant input exists and doesn't match, wait for it to update
              if (variantInput && variantInput.value && variantInput.value !== variant.id.toString()) {
                return false; // Not ready yet, variant picker needs to update
              }
              
              // Update button state (it will get price from price-list element)
              updateButtonState(variant);
              return true; // Success
            }
          } catch (e) {
            console.warn("Could not parse initial variant data:", e);
          }
        }
      }
      
      return false; // Not ready yet
    }
    
    // Try immediately
    let attempts = 0;
    const maxAttempts = 10; // Try for up to 1 second (10 * 100ms)
    
    function attemptUpdate() {
      if (tryUpdateButton()) {
        return; // Success
      }
      
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(attemptUpdate, 100);
      }
    }
    
    attemptUpdate();
  });
  
  // Also listen for variant input changes (catches when variant picker updates from URL)
  document.addEventListener("DOMContentLoaded", function() {
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput) {
      // Listen for changes to the variant input
      variantInput.addEventListener("change", function() {
        // Skip if this is a product with grouping
        const productGrouping = document.querySelector("product-grouping");
        if (productGrouping) {
          return;
        }
        
        // Get variant from variant picker script
        const variantPicker = document.querySelector("variant-picker");
        if (variantPicker) {
          const variantScript = variantPicker.querySelector("script[data-variant]");
          if (variantScript) {
            try {
              const variant = JSON.parse(variantScript.textContent || "{}");
              if (variant && variant.id && variant.id.toString() === variantInput.value) {
                updateButtonState(variant);
              }
            } catch (e) {
              console.warn("Could not parse variant data from input change:", e);
            }
          }
        }
      });
    }
  });
};

ShopifyCustom.setVariantOnLoad = function () {
  function updateVariantIdFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get("variant");
    if (!variantId) return;

    const stickyEl = document.getElementById("constructor-product-info");
    if (stickyEl) {
      stickyEl.setAttribute("data-cnstrc-item-variation-id", variantId);
    }
  }

  // Run on initial load
  document.addEventListener("DOMContentLoaded", updateVariantIdFromUrl);

  // Patch history.pushState and history.replaceState to also trigger update
  ["pushState", "replaceState"].forEach((method) => {
    const original = history[method];
    history[method] = function () {
      original.apply(this, arguments);
      updateVariantIdFromUrl();
    };
  });

  // Handle back/forward nav
  window.addEventListener("popstate", updateVariantIdFromUrl);
};

/* === Feature Modules === */
window.ShopifyCustom = window.ShopifyCustom || {};
/**
 * Helper: Detect if current product is tagged as "preorder"
 * Looks for #main-product-info.preorder or data-preorder="true"
 */
window.isPreorderProduct = function () {
  const el = document.querySelector('#main-product-info');
  return !!(el && (el.classList.contains('preorder') || el.dataset.preorder === 'true'));
};


// Back-to-top: shows only while scrolling DOWN past threshold; hides on up/mini-cart/short pages
ShopifyCustom.backToTop = function (userOpts = {}) {
  const btn = document.getElementById("btt");
  if (!btn || btn.__bttInit) return;
  btn.__bttInit = true;

  const ds = btn.dataset || {};
  const opts = Object.assign(
    {
      threshold: Number(ds.threshold || 200),
      dirEps: 4,
      bodyOpenClasses: [
        "cart-drawer-open",
        "drawer-open",
        "js-drawer-open",
        "cart-open",
      ],
      drawerSelectors: [
        ".js-cartDrawer.is-open",
        ".drawer.is-open",
        '[data-minicart-open="true"]',
        "dialog[open][data-cart-drawer]",
        '[aria-modal="true"]:not([aria-hidden="true"])',
        "#CartDrawer.is-open",
        ".CartDrawer.is-open",
        ".cart-drawer.is-open",
      ],
    },
    userOpts
  );

  const prefersReduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const isVisibleEl = (el) => !!(el && el.offsetParent !== null);
  const isDrawerOpen = () =>
    opts.bodyOpenClasses.some((c) => document.body.classList.contains(c)) ||
    opts.drawerSelectors.some((sel) =>
      isVisibleEl(document.querySelector(sel))
    );

  const pageTooShort = () =>
    document.documentElement.scrollHeight - window.innerHeight <=
    opts.threshold + 20;

  const show = () => {
    btn.classList.add("btt--visible");
    btn.classList.remove("btt--hidden");
    btn.setAttribute("aria-hidden", "false");
  };
  const hide = () => {
    btn.classList.remove("btt--visible");
    btn.classList.add("btt--hidden");
    btn.setAttribute("aria-hidden", "true");
  };

  // ↓ Buffer so it doesn't vanish on tiny upward nudges / jitter
  const HIDE_AFTER_UP = 96; // px of upward travel required to hide
  //const IDLE_HIDE_MS  = 1200; // also auto-hide after 1.2s without scrolling
  let upAccum = 0;
  let idleId = null;

  let lastY = window.scrollY || document.documentElement.scrollTop;
  let ticking = false;

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY || document.documentElement.scrollTop;
      const dy = y - lastY;
      const goingDown = dy > opts.dirEps;
      const goingUp = dy < -opts.dirEps;

      const canShow = y > opts.threshold && !pageTooShort() && !isDrawerOpen();

      // Show only when scrolling down and eligible
      if (goingDown && canShow) {
        show();
        upAccum = 0;
      }

      // Hide only after a meaningful upward scroll, or if back under threshold / not eligible
      if (goingUp) {
        upAccum += -dy; // accumulate upward distance
        if (upAccum >= HIDE_AFTER_UP || y <= opts.threshold || !canShow) {
          hide();
          upAccum = 0;
        }
      }

      // Idle auto-hide
      if (idleId) clearTimeout(idleId);
      // if (canShow) idleId = setTimeout(() => { hide(); upAccum = 0; }, IDLE_HIDE_MS);

      lastY = y;
      ticking = false;
    });
  };

  [
    ["scroll", onScroll],
    ["resize", onScroll],
    ["load", onScroll],
  ].forEach(([e, f]) => window.addEventListener(e, f, { passive: true }));
  document.addEventListener("DOMContentLoaded", onScroll);
  ["open", "close", "show", "hide"].forEach((s) => {
    window.addEventListener(`theme:cart:${s}`, onScroll, { passive: true });
    window.addEventListener(`theme:drawer:${s}`, onScroll, { passive: true });
  });

  btn.addEventListener("click", () => {
    hide();
    prefersReduced
      ? window.scrollTo(0, 0)
      : window.scrollTo({ top: 0, behavior: "smooth" });
    window.dataLayer && window.dataLayer.push({ event: "back_to_top_clicked" });
  });

  hide();
};

/* === Feature Modules === */
//Sample Function
// ShopifyCustom.homeHeroAnimation = function () {
//   if (!document.body.classList.contains('template-index')) return;

//   const hero = document.querySelector('.hero-banner');
//   if (hero) {
//     hero.classList.add('fade-in');
//   }
// };

/**
 * Native Lazy Loading Blur-Up Effect
 * Mimics lazySizes blur-up but uses native lazy loading for better performance
 * Handles the transition from LQIP background to full image
 */
ShopifyCustom.initNativeLazyLoadBlurUp = function () {
  const handleImageLoad = (img, timeoutId) => {
    // Avoid duplicate processing
    if (img.classList.contains("lazyloaded")) return;

    // Clear timeout if it exists
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    img.classList.add("lazyloaded");
    // Remove background image after transition completes to save memory
    setTimeout(() => {
      img.style.backgroundImage = "none";
    }, 450); // Slightly longer than CSS transition
  };

  const processImage = (img) => {
    // Skip if already processed
    if (img.dataset.blurProcessed) return;
    img.dataset.blurProcessed = "true";

    // Check if image is already loaded (cached or already in viewport)
    if (img.complete && img.naturalHeight > 0) {
      // Image already loaded
      handleImageLoad(img);
      return;
    }

    // Single timeout as ultimate fallback (no polling)
    const timeoutId = setTimeout(() => {
      // Force unblur after timeout regardless of load state
      if (!img.classList.contains("lazyloaded")) {
        handleImageLoad(img);
      }
    }, 10000);

    // Event-driven approach - reliable in all modern browsers
    const onLoadOrError = () => {
      handleImageLoad(img, timeoutId);
    };

    img.addEventListener("load", onLoadOrError, { once: true, passive: true });
    img.addEventListener("error", onLoadOrError, { once: true, passive: true });
  };

  // Process all existing blur-up-native images
  // Note: Some carousels remove loading="lazy" dynamically, so we select all blur-up-native images
  const images = document.querySelectorAll("img.blur-up-native");
  images.forEach(processImage);

  // Handle dynamically added images (AJAX, quick view, Constructor.io)
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          // Check if the node itself is a blur-up image
          if (
            node.matches?.("img.blur-up-native") &&
            !node.dataset.blurProcessed
          ) {
            processImage(node);
          }

          // Check for blur-up images within the node
          const lazyImages = node.querySelectorAll?.(
            "img.blur-up-native:not([data-blur-processed])"
          );
          if (lazyImages?.length) {
            lazyImages.forEach(processImage);
          }
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

ShopifyCustom.addSortModalCloseButton = function () {
  // Observer to watch for Constructor.io sort modals
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          // Check if this is a sort modal container
          const sortModal = node.classList?.contains("cio-plp-sort")
            ? node
            : node.querySelector?.(".cio-plp-sort");

          if (sortModal) {
            const modalContent = sortModal.querySelector(
              ".cio-mobile-modal-content"
            );
            const modalBackdrop = sortModal.querySelector(
              ".cio-mobile-modal-backdrop"
            );

            if (
              modalContent &&
              !modalContent.querySelector(".cio-sort-close-button")
            ) {
              // Create close button
              const closeButton = document.createElement("button");
              closeButton.className = "cio-sort-close-button";
              closeButton.setAttribute("aria-label", "Close sort options");
              closeButton.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                  <path d="M18.75 6.25L6.25 18.75M6.25 6.25L18.75 18.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              `;

              // Close modal on button click
              closeButton.addEventListener("click", () => {
                if (modalBackdrop) {
                  modalBackdrop.click();
                }
              });

              // Insert as first child
              modalContent.insertBefore(closeButton, modalContent.firstChild);
            }
          }
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
};


/* === Initialize after DOM is ready === */
// Install empty-hash guard at module evaluate time (modules are deferred, before DOMContentLoaded).
ShopifyCustom.runSafe(
  "preventEmptyHashHistoryEntries",
  ShopifyCustom.preventEmptyHashHistoryEntries
);

document.addEventListener('DOMContentLoaded', () => {
    ShopifyCustom.init();
});
